<template>
  <div id="app">
    <v-container fluid class="fill-height">
      <v-row align="center" justify="center">
        <v-col cols="12" sm="8" md="6" lg="4">
          <!-- Carte de confirmation -->
          <v-card class="mx-auto rounded-lg" elevation="8">
            <!-- En-tête avec animation de succès -->
            <v-card-title class="white--text py-5 text-center d-block" style="background: #009688">
              <v-icon size="60" color="white" class="mb-2">
                {{ paymentSuccess ? 'mdi-check-circle-outline' : 'mdi-alert-circle-outline' }}
              </v-icon>
              <h2 class="text-h4 font-weight-medium">
                {{ paymentSuccess ? 'Paiement Réussi' : 'Validation en cours ...' }}
              </h2>
            </v-card-title>

            <!-- Animation lors du chargement -->
            <v-card-text v-if="loading" class="text-center py-5">
              <v-progress-circular
                  indeterminate
                  color="#009688"
                  size="64"
              ></v-progress-circular>
              <p class="mt-4 text-body-1">Vérification du paiement en cours...</p>
            </v-card-text>

            <!-- Détails du paiement -->
            <v-card-text v-else class="pa-5">
              <!-- Message de confirmation -->
              <p class="text-body-1 text-center mb-6">
                {{
                  paymentSuccess ?
                      'Votre transaction a été traitée avec succès. Merci pour votre confiance!' :
                      'Un problème est survenu lors du traitement de votre paiement. Veuillez réessayer ou contacter le support.'
                }}
              </p>

              <!-- Informations de la transaction -->
              <v-list v-if="paymentSuccess" two-line>
                <v-list-item>
                  <v-list-item-icon>
                    <v-icon color="#009688">mdi-account</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Nom & Prénom</v-list-item-title>
                    <v-list-item-subtitle>{{ customerFullName }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-divider class="my-2"></v-divider>

                <v-list-item>
                  <v-list-item-icon>
                    <v-icon color="#009688">mdi-shopping-outline</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Produit</v-list-item-title>
                    <v-list-item-subtitle>{{ productName }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-divider class="my-2"></v-divider>

                <v-list-item>
                  <v-list-item-icon>
                    <v-icon color="#009688">mdi-cash</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Montant</v-list-item-title>
                    <v-list-item-subtitle class="font-weight-bold">{{ formattedAmount }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-divider class="my-2"></v-divider>

                <v-list-item>
                  <v-list-item-icon>
                    <v-icon color="#009688">mdi-calendar-check</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Date & Heure</v-list-item-title>
                    <v-list-item-subtitle>{{ transactionDate }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>

                <v-divider class="my-2"></v-divider>

                <v-list-item>
                  <v-list-item-icon>
                    <v-icon color="#009688">mdi-identifier</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content>
                    <v-list-item-title>Référence</v-list-item-title>
                    <v-list-item-subtitle>{{ transactionId }}</v-list-item-subtitle>
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-card-text>

            <!-- Actions -->
            <v-card-actions class="px-6 pb-6">
              <v-spacer></v-spacer>
              <v-btn
                  block
                  x-large
                  color="#009688"
                  dark
                  v-if="paymentSuccess"
                  rounded
                  elevation="2"
                  @click="goToHome"
              >
                <v-icon left>mdi-home</v-icon>
                Vérifier le solde personnel
              </v-btn>
              <v-btn
                  v-else-if="bRetryPayment"
                  color="orange darken-3"
                  rounded
                  elevation="2"
                  block
                  @click="retryPayment"
              >
                <v-icon left>mdi-refresh</v-icon>
                Réessayer
              </v-btn>
            </v-card-actions>
          </v-card>

          <!-- Illustration de paiement -->
          <v-img
              v-if="paymentSuccess"
              src="https://cdn.jsdelivr.net/gh/Kasiyliy/payment-assets/payment-success.svg"
              max-height="150"
              contain
              class="mt-10"
          ></v-img>
        </v-col>
      </v-row>

      <!-- Add this for users who try to access the page again -->
      <v-dialog v-model="showAlreadyProcessedDialog" persistent max-width="500">
        <v-card>
          <v-card-title class="headline">
            <v-icon left color="orange darken-2">mdi-information-outline</v-icon>
            {{titleMessageError}}
          </v-card-title>
          <v-card-text>
            {{messageError}}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="primary" text @click="goToVeriferCompte">{{btTextError}}</v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

      <!-- Message d'alerte -->
      <v-snackbar
          v-model="snackbar"
          :color="snackbarColor"
          timeout="5000"
          centered
          top
      >
        {{ snackbarText }}
        <template v-slot:action="{ attrs }">
          <v-btn
              text
              v-bind="attrs"
              @click="snackbar = false"
          >
            Fermer
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>

<script>
import {invoice} from "./paydunyaService";
import moment from "moment/moment";
import apiClient from "../../../config/api.js";

export default {
  name: 'PaymentSuccess',
  data() {
    return {
      token: null,
      transactionId: null,
      orderDetails: null,
      loading: true,
      paymentSuccess: false,
      bRetryPayment: false,
      customerFullName: '',
      productName: '',
      amount: 0,
      transactionDate: '',
      snackbar: false,
      snackbarText: '',
      snackbarColor: 'info',
      showAlreadyProcessedDialog: false,
      messageError:'',
      titleMessageError:'',
      btTextError:"Vérifiez votre compte",

      isVerified: false,
      error: null,
      paymentDetails: null,
      pollingCount: 0,
      maxPolls: 10 // Maximum de tentatives de vérification
    }
  },
  computed: {
    formattedAmount() {
      return new Intl.NumberFormat('fr-FR', {
        style: 'currency',
        currency: 'XOF',
        minimumFractionDigits: 0
      }).format(this.amount);
    }
  },
  created() {
    setTimeout(() => {
      this.fetchPaymentStatus();
    }, 1500);
  },
  methods: {
    goToVeriferCompte() {
      if (this.btTextError !== "Rafraîchir la page")
      this.$router.push('/agents/rapportssalarie')
      else
        this.$router.go(0)
    },

    fetchPaymentStatus() {
      // Récupérer le tokenPaydunya depuis l'URL
      const urlParams = new URLSearchParams(window.location.search);
      const tokenPaydunya = urlParams.get('token');

      if (!tokenPaydunya) {
        this.showErrorMessage("Token de transaction manquant");
        this.loading = false;
        this.messageError = "Token de transaction manquant";
        this.titleMessageError = "Données manquantes";
        this.showAlreadyProcessedDialog = true;
        return;
      }

      // Appel à l'API Paydunya (à implémenter selon votre environnement)
      invoice.confirm(tokenPaydunya)
          .then(() => {
            const dataToSave = {
              token: tokenPaydunya,
              userId: sessionStorage.getItem("userId")
            };

            apiClient
                .post(
                    `${this.$store.getters.user.baseUrl}/paydunya/verify-frontend-payment`,
                    dataToSave
                )
                .then(res => {
                  const data = res.data;

                  if (data.status === 'success') {
                    // Paiement vérifié avec succès
                    this.isVerified = true;
                    this.loading = false;
                    this.paymentDetails = data;
                    // Stocker une preuve de paiement côté client (optionnel, avec une date d'expiration)
                    const paymentProof = {
                      tokenPaydunya: tokenPaydunya,
                      transactionId: data.transactionId,
                      transactionDate: data.date,
                      verifiedAt: new Date().toISOString(),
                      expiresAt: new Date(Date.now() + 3600000).toISOString(), // expire dans 1h
                      customerFullName: invoice.customer["name"],
                      productName: "Rechargement compte personnel",
                      amount: data.amount
                    };
                    sessionStorage.setItem('lastVerifiedPayment', JSON.stringify(paymentProof));

                    this.customerFullName = invoice.customer["name"];
                    this.productName = "Rechargement compte personnel";
                    this.amount = data.amount;
                    this.transactionId = data.transactionId;
                    this.transactionDate = data.date;

                    // Rediriger vers la page appropriée ou afficher un message de succès
                    console.log(invoice.status);
                    console.log(invoice.responseText);  // Réponse du serveur

                    // Les champs qui suivent seront disponibles si et
                    // seulement si le statut du paiement est égal à "completed".

                    // Vous pouvez récupérer le nom, l'adresse email et le
                    // numéro de téléphone du client en utilisant l'objet suivant
                    console.log(invoice.customer); // {name: 'Alioune', phone: '773830274', email: 'aliounebadara@gmail.com'}

                    // URL du reçu PDF électronique pour téléchargement
                    //console.log(invoice.receiptURL); // 'https://app.paydunya.com/sandbox-checkout/receipt/pdf/test_VPGPZNnHOC.pdf'
                    //window.open(invoice.receiptURL, '_blank');

                    //this.loadOrderDetails();

                    this.paymentSuccess = true;
                    this.showSuccessMessage("Paiement confirmé avec succès");
                    sessionStorage.setItem('pendingOrder', null);
                  } else if (data.status === 'pending' || data.status === 'processing') {
                    // Le paiement est en cours de traitement, réessayer après un délai
                    this.pollingCount++;

                    if (this.pollingCount < this.maxPolls) {
                      setTimeout(() => {
                        this.fetchPaymentStatus();
                      }, 2000); // Réessayer toutes les 2 secondes
                    } else {
                      this.loading = false;
                      this.error = "Le paiement prend plus de temps que prévu. Veuillez actualiser la page.";
                      this.messageError = this.error;
                      this.titleMessageError = "En attente de validation serveur";
                      this.showAlreadyProcessedDialog = true;
                      this.btTextError = "Rafraîchir la page";
                    }
                  } else {
                    // Échec de la vérification
                    this.loading = false;
                    this.error = data.message || "Échec lors de la vérification du paiement.";
                    this.messageError = this.error;
                    this.titleMessageError = "Transaction rejetée";
                    this.showAlreadyProcessedDialog = true;
                    this.btTextError = "Retourner à votre compte";
                  }
                })
                .catch(error => {
                  console.error("Erreur lors de la vérification du paiement:", error);
                  this.paymentSuccess = false;
                  this.bRetryPayment = true;
                  this.messageError = error.response.data.message;
                  this.titleMessageError = "Transaction rejetée";
                  this.showAlreadyProcessedDialog = true;
                  this.btTextError = "Retourner à votre compte";
                  //this.showErrorMessage("Impossible de vérifier le statut du paiement");
                });
          })
          .catch(error => {
            console.error("Erreur lors de la vérification du paiement:", error);
            this.paymentSuccess = false;
            this.bRetryPayment = true;
            this.messageError = "Il n'est pas possible de confirmer cet achat";
            this.titleMessageError = "Transaction non trouvée";
            this.btTextError = "Retourner à votre compte";
            this.showAlreadyProcessedDialog = true;
           // this.showErrorMessage("Impossible de vérifier le statut du paiement");
          }).finally(() => {
        this.loading = false;
      });
    },

    isEmpty(str) {
      return (!str || 0 === str.length || /^\s*$/.test(str));
    },

    loadOrderDetails() {
      const storedOrder = sessionStorage.getItem('pendingOrder')

      if (storedOrder) {
        try {
          this.orderDetails = JSON.parse(storedOrder)
          this.customerFullName = invoice.customer["name"];
          this.productName = "Rechargement compte personnel";
          this.amount = this.orderDetails["total"];
        } catch (error) {
          console.error('Error parsing order pending:', error)
        }
      }
    },

    goToHome() {
      window.location.href = process.env.VUE_APP_JABALI_WEB_BASE_URL + '/agents/rapportssalarie';
    },

    retryPayment() {
      // Rediriger vers la page de paiement
      window.location.href = process.env.VUE_APP_JABALI_WEB_BASE_URL + '/paydunya/paymentinitiation'; // À adapter selon votre structure
    },

    showSuccessMessage(message) {
      this.snackbarText = message;
      this.snackbarColor = 'success';
      this.snackbar = true;
    },

    showErrorMessage(message) {
      this.snackbarText = message;
      this.snackbarColor = 'error';
      this.snackbar = true;
    }
  }
};
</script>

<style scoped>
</style>